import { ExternalLink, Award, Info, Briefcase, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import { OfficialLeo5 } from "@/components/app/degree/OfficialLeo5";

/**
 * Phase-1 HESA (Discover Uni 2024/25) UI blocks for DegreeDetail.
 *
 *   - TefBadge          → gov TEF rating (Section 5)
 *   - HesaAccreditations → professional-body list w/ URLs (Section 6)
 *   - TopGraduateJobs   → chip list from JOBLIST.csv (Section 8)
 *   - LeoComparison     → LEO3 vs LEO5 side-by-side (Section 8)
 *   - UcasPathways      → "Apply via UCAS" buttons (Section 13)
 *
 * Every component fails silently (returns null) when its slice of
 * `hesa_stats` is missing / suppressed, so degrees without a signal
 * simply don't render the block.
 */

const HESA_ATTR = "HESA Discover Uni 2024/25 · CC-BY-4.0";

const TEF_COLOR = {
  Gold:                    "bg-amber-100 text-amber-900 border-amber-300",
  Silver:                  "bg-stone-100 text-stone-800 border-stone-300",
  Bronze:                  "bg-orange-100 text-orange-900 border-orange-300",
  "Requires improvement":  "bg-red-100 text-red-900 border-red-300",
};

export function TefBadge({ tef }) {
  if (!tef?.overall) return null;
  const color = TEF_COLOR[tef.overall] || "bg-stone-100 text-stone-800 border-stone-300";
  return (
    <div data-testid="tef-badge" className="mb-5 flex items-start gap-3 flex-wrap">
      <div className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 ${color}`}>
        <Award className="h-4 w-4" />
        <span className="font-underground-sc tracking-wider text-[10px] opacity-70">TEF · Gov.uk</span>
        <span className="font-semibold text-sm">{tef.overall}</span>
      </div>
      {tef.student_experience && (
        <div data-testid="tef-student-experience" className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-2.5 py-1 text-xs text-stone-700">
          <span className="font-underground-sc tracking-wider text-[9px] text-stone-500">Student experience</span>
          <span className="font-semibold">{tef.student_experience}</span>
        </div>
      )}
      {tef.student_outcomes && (
        <div data-testid="tef-student-outcomes" className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-2.5 py-1 text-xs text-stone-700">
          <span className="font-underground-sc tracking-wider text-[9px] text-stone-500">Student outcomes</span>
          <span className="font-semibold">{tef.student_outcomes}</span>
        </div>
      )}
      {tef.url && (
        <a
          href={tef.url}
          target="_blank"
          rel="noreferrer"
          data-testid="tef-outcome-link"
          className="inline-flex items-center gap-1 text-xs text-[#4C1D95] hover:underline"
        >
          View full TEF outcome <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

export function HesaAccreditations({ accs }) {
  if (!accs || accs.length === 0) return null;
  return (
    <div data-testid="hesa-accreditations" className="mt-2 space-y-2">
      <div className="label-uppercase text-stone-600">Professional accreditations (HESA)</div>
      {accs.map((a, i) => (
        <Card key={a.code || i} data-testid={`hesa-acc-${i}`} className="p-3 border-stone-200 bg-white">
          <div className="text-sm text-stone-800">{a.text}</div>
          <div className="mt-1 flex items-center gap-3 flex-wrap text-xs">
            {a.url && (
              <a
                href={a.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[#4C1D95] hover:underline"
              >
                {new URL(a.url.startsWith("http") ? a.url : `https://${a.url}`).hostname} <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {a.dependent_on_choice && (
              <span className="inline-flex items-center gap-1 text-amber-800">
                <Info className="h-3 w-3" /> Depends on module / pathway choice
              </span>
            )}
          </div>
        </Card>
      ))}
      <div className="text-[10px] font-mono text-stone-400 mt-1">{HESA_ATTR}</div>
    </div>
  );
}

export function TopGraduateJobs({ jobs }) {
  if (!jobs || jobs.length === 0) return null;
  // Filter out zero-percent noise; keep top-ranked and any HS-flagged row.
  const shown = jobs
    .filter((j) => (j.percentage ?? 0) > 0 || j.highly_skilled)
    .slice(0, 8);
  if (shown.length === 0) return null;

  // Split highly-skilled vs other, then take totals for the sub-header.
  const hsTotal = shown
    .filter((j) => j.highly_skilled)
    .reduce((s, j) => s + (j.percentage ?? 0), 0);

  // Chart scale: longest bar = biggest cohort we display.
  const maxPct = Math.max(...shown.map((j) => j.percentage || 0), 1);

  return (
    <div data-testid="top-graduate-jobs" className="mt-4">
      <div className="label-uppercase text-stone-600 mb-2 flex items-center gap-1.5">
        <Briefcase className="h-3 w-3" /> Top graduate jobs (HESA)
        {hsTotal > 0 && (
          <span
            data-testid="grad-jobs-hs-summary"
            className="ml-2 font-mono normal-case tracking-normal text-[10px] text-emerald-800"
          >
            · {Math.round(hsTotal)}% enter highly-skilled roles
          </span>
        )}
      </div>
      <div className="space-y-1.5" data-testid="grad-jobs-rows">
        {shown.map((j, i) => {
          const pct = j.percentage || 0;
          const barPct = (pct / maxPct) * 100;
          return (
            <div
              key={i}
              data-testid={`grad-job-${i}`}
              className="grid grid-cols-[1.5rem_1fr_3rem] items-center gap-2 text-[12px]"
            >
              <div className="font-underground-sc text-[9px] text-stone-500 text-right">
                #{j.order}
              </div>
              <div className="relative h-6 bg-stone-100 rounded-sm overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-sm ${
                    j.highly_skilled ? "bg-emerald-600/70" : "bg-stone-400/70"
                  }`}
                  style={{ width: `${barPct}%` }}
                />
                <div className="relative flex items-center h-full px-2 text-stone-900">
                  <span className="truncate" title={j.job}>{j.job}</span>
                  {j.highly_skilled && (
                    <span
                      className="ml-1.5 font-mono text-[9px] uppercase tracking-wider text-emerald-900 bg-emerald-100 border border-emerald-300 rounded-sm px-1 py-0.5"
                      data-testid={`grad-job-${i}-hs-badge`}
                    >
                      HS
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right font-mono tabular-nums text-[#4C1D95]">
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] font-mono text-stone-400 mt-2">
        HS = Highly-skilled (ONS SOC 2020 classification) · {HESA_ATTR}
      </div>
    </div>
  );
}

export function LeoComparison({ hesa, employerLifts, official }) {
  const leo3 = hesa?.leo3_median_gbp;
  const leo5 = hesa?.leo5_median_gbp;
  if (!leo3 && !leo5 && !official) return null;
  // Official five-year rows are subject-group figures from a newer release; a growth % against LEO3 would mix releases.
  const growth = !official && leo3 && leo5 ? Math.round(((leo5 - leo3) / leo3) * 100) : null;

  // Employer lift overlay — same map the DegreeDetail chiplist uses.
  // Only firms with a resolved `lift_pct` render a marker; the
  // component gracefully renders without markers when the parent
  // didn't pass any lifts (e.g. non-UK/US degrees or fresh page loads
  // before the batch endpoint responds).
  const lifts = employerLifts && typeof employerLifts === "object"
    ? Object.values(employerLifts).filter((v) => v && typeof v.lift_pct === "number")
    : [];
  const overlayMax = Math.max(growth ?? 0, ...lifts.map((l) => l.lift_pct), 10);

  return (
    <div data-testid="leo-comparison" className="mt-5 rounded-lg border border-stone-200 bg-white p-4">
      <div className="label-uppercase text-stone-600 mb-3 flex items-center gap-1.5">
        <TrendingUp className="h-3 w-3" /> Median salary: 3-year vs 5-year (LEO, Gov.uk)
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div data-testid="leo3-block">
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">3 years after graduation</div>
          <div className="font-chip text-2xl font-semibold text-stone-900 mt-1">{leo3 ? `£${leo3.toLocaleString()}` : "—"}</div>
          {hesa?.leo3_sample_size && (
            <div className="text-[10px] font-mono text-stone-400 mt-0.5">n = {hesa.leo3_sample_size}</div>
          )}
        </div>
        <div data-testid="leo5-block">
          <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">5 years after graduation</div>
          {official ? <OfficialLeo5 official={official} /> : (
            <>
              <div className="font-chip text-2xl font-semibold text-[#4C1D95] mt-1">{leo5 ? `£${leo5.toLocaleString()}` : "—"}</div>
              {hesa?.leo5_sample_size && (
                <div className="text-[10px] font-mono text-stone-400 mt-0.5">n = {hesa.leo5_sample_size}</div>
              )}
            </>
          )}
        </div>
      </div>
      {growth != null && (
        <div className="mt-3 text-sm">
          <span className="label-uppercase !text-[10px] mr-2">Growth over year 3–5</span>
          <span className={`font-mono font-semibold ${growth >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {growth >= 0 ? "+" : ""}{growth}%
          </span>
        </div>
      )}

      {/* Employer overlay — a compact horizontal bar showing the
          course-average growth vs each top employer's aggregated
          lift. The purple tick is the course itself; green ticks are
          named employers (McKinsey +40%, Google DeepMind +35% etc).
          This surfaces per-firm progression right next to the
          per-course number so families see "average +34%, but
          McKinsey grads +40%" without having to click through. */}
      {lifts.length > 0 && growth != null && (
        <div className="mt-5 pt-4 border-t border-stone-100" data-testid="leo-employer-overlay">
          <div className="label-uppercase text-stone-500 mb-3 !text-[10px]">
            How top employers' grads compare on lift
          </div>
          <div className="relative h-8">
            <div className="absolute inset-x-0 top-3 h-1 rounded-full bg-stone-100" />
            {/* Course average marker (purple) */}
            <div
              className="absolute top-2 h-3 w-0.5 bg-[#4C1D95] rounded-sm"
              style={{ left: `${Math.min(100, (growth / overlayMax) * 100)}%` }}
              title={`Course average · +${growth}%`}
              aria-hidden
            />
            {/* Employer markers (emerald) */}
            {lifts.map((l) => (
              <div
                key={l.employer}
                data-testid={`leo-employer-tick-${l.employer.replace(/\s+/g, '-').toLowerCase()}`}
                className="absolute top-2 h-3 w-0.5 bg-emerald-600 rounded-sm cursor-help"
                style={{ left: `${Math.min(100, (l.lift_pct / overlayMax) * 100)}%` }}
                title={`${l.employer} · +${l.lift_pct}%`}
              />
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono text-stone-600">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-0.5 h-3 bg-[#4C1D95]" /> course +{growth}%
            </span>
            {lifts.map((l) => (
              <span
                key={l.employer}
                className="inline-flex items-center gap-1"
              >
                <span className="inline-block w-0.5 h-3 bg-emerald-600" />
                {l.employer} +{l.lift_pct}%
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="text-[10px] font-mono text-stone-400 mt-2">{official ? `3-year figure: ${HESA_ATTR}` : HESA_ATTR}</div>
    </div>
  );
}

export function UcasPathways({ pathways, uniName, courseTitle }) {
  if (!pathways || pathways.length === 0) return null;
  // HESA's `ucas_id` values are its internal KIS programme identifiers,
  // NOT the letter+digits UCAS course codes shown on the applicant
  // portal — so linking `coursedisplay/courses/{id}` 404s. We instead
  // send users to UCAS' unified search pre-filled with the university
  // name + course title, which always resolves to the live provider
  // page. The KIS id stays visible as a reference badge.
  const searchQuery = [uniName, courseTitle].filter(Boolean).join(" ").trim();
  const searchHref = searchQuery
    ? `https://digital.ucas.com/search?query=${encodeURIComponent(searchQuery)}`
    : "https://digital.ucas.com/search";
  return (
    <div data-testid="ucas-pathways" className="mb-5">
      <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
        <div className="label-uppercase text-stone-600">Apply via UCAS</div>
        <a
          href={searchHref}
          target="_blank"
          rel="noreferrer"
          data-testid="ucas-search-cta"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] px-3 py-1.5 text-[11px] font-underground-sc tracking-wider text-white hover:bg-[#3B0764] transition-colors"
        >
          Search this course on UCAS
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="flex flex-wrap gap-2">
        {pathways.map((p, i) => (
          <span
            key={i}
            data-testid={`ucas-pathway-${i}`}
            className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-stone-50 px-3 py-1.5 text-sm text-stone-700"
            title="HESA KIS programme identifier — use the search button above to open UCAS."
          >
            <span className="font-underground-sc tracking-wider text-[9px] opacity-70">KIS</span>
            <span className="font-mono font-semibold">{p.ucas_id}</span>
            {p.location_id && <span className="text-[11px] opacity-70">· campus {p.location_id}</span>}
          </span>
        ))}
      </div>
      <div className="text-[10px] font-mono text-stone-400 mt-1">{HESA_ATTR}</div>
    </div>
  );
}
